import { useAuthActions, useCurrentUser, useOrganization, useOrganizationList } from '@origens/auth'
import { subscribeNavigation } from '@go-beyond/react/browser'
import { ThemeMenuItem } from '@origens/ui/theme'
import { Building2, Check, ChevronDown, Menu, Search, UserRound } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { listProjects, type PortalProject } from '../lib/portal-api.js'
import {
  PORTAL_HEADER_ACCOUNT_SLOT_ID,
  PORTAL_HEADER_TITLE_SLOT_ID,
} from './portal-header-shell.js'
import type { PortalShellData } from './portal-sidebar-client.js'
import { Input } from './ui/input.js'

function useHeaderSlot(slotId: string) {
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setSlot(document.getElementById(slotId))
  }, [slotId])
  return slot
}

function currentPathname() {
  return typeof window === 'undefined' ? '/' : window.location.pathname
}

function projectSlugFromPath(pathname: string) {
  const match = pathname.match(/^\/projects\/([^/]+)/)
  return match?.[1] ? decodeURIComponent(match[1]) : ''
}

const PROJECT_SEARCH_THRESHOLD = 6

/** Persistent organization/project context switcher beside the Origens wordmark. */
export function PortalContextSwitcher({ shell }: { shell?: PortalShellData | undefined }) {
  const titleSlot = useHeaderSlot(PORTAL_HEADER_TITLE_SLOT_ID)
  const { organization } = useOrganization()
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  })
  const detailsRef = useRef<HTMLDetailsElement>(null)
  const [open, setOpen] = useState(false)
  const [pathname, setPathname] = useState(currentPathname)
  const [projects, setProjects] = useState<PortalProject[]>(() =>
    (shell?.projects ?? []).map((project) => ({ ...project } as PortalProject)),
  )
  const [projectQuery, setProjectQuery] = useState('')
  const [working, setWorking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const projectSlug = useMemo(() => projectSlugFromPath(pathname), [pathname])
  const project = projects.find((candidate) => candidate.slug === projectSlug)
  const filteredProjects = useMemo(() => {
    const query = projectQuery.trim().toLocaleLowerCase()
    if (!query) return projects
    return projects.filter(
      (candidate) =>
        candidate.name.toLocaleLowerCase().includes(query) ||
        candidate.slug.toLocaleLowerCase().includes(query),
    )
  }, [projectQuery, projects])
  const organizationName = shell?.organizationName || organization?.name || 'Organization'
  const contextLabel = projectSlug
    ? `${organizationName} / ${project?.name ?? shell?.contextName ?? projectSlug}`
    : organizationName

  useEffect(() => {
    const update = () => setPathname(currentPathname())
    const unsubscribe = subscribeNavigation((event) => {
      if (event.type === 'success') update()
    })
    window.addEventListener('popstate', update)
    return () => {
      unsubscribe()
      window.removeEventListener('popstate', update)
    }
  }, [])

  useEffect(() => {
    if (shell) {
      setProjects(shell.projects.map((project) => ({ ...project } as PortalProject)))
      return
    }
    if (!open || !organization?.id) return
    let active = true
    void listProjects()
      .then((response) => {
        if (active) setProjects(response.projects)
      })
      .catch(() => {
        if (active) setProjects([])
      })
    return () => {
      active = false
    }
  }, [open, organization?.id, shell])

  useEffect(() => {
    if (!open) return
    function closeOnOutsidePointer(event: PointerEvent) {
      const target = event.target
      if (target instanceof Node && !detailsRef.current?.contains(target)) setOpen(false)
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setOpen(false)
      detailsRef.current?.querySelector('summary')?.focus()
    }
    document.addEventListener('pointerdown', closeOnOutsidePointer)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  async function selectOrganization(id: string) {
    if (id === organization?.id) {
      setOpen(false)
      if (projectSlug) window.location.assign('/')
      return
    }
    setWorking(true)
    setError(null)
    try {
      await setActive?.({ organization: id })
      window.location.assign('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch organization')
      setWorking(false)
    }
  }

  return titleSlot
    ? createPortal(
        <details
          ref={detailsRef}
          open={open}
          onToggle={(event) => {
            setOpen(event.currentTarget.open)
            if (!event.currentTarget.open) setProjectQuery('')
          }}
          className="group relative"
        >
          <summary className="flex max-w-[12rem] cursor-pointer list-none items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--muted)] px-2.5 py-1 text-xs font-medium text-[var(--ink-muted)] transition hover:text-[var(--ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] sm:max-w-[22rem] [&::-webkit-details-marker]:hidden">
            <span className="truncate">{contextLabel}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 transition group-open:rotate-180" />
          </summary>
          <div
            className="absolute left-0 z-[80] mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-lg border border-[var(--border)] bg-[var(--card)] p-2 shadow-2xl sm:w-[30rem] lg:w-[36rem]"
            style={{ backgroundColor: 'var(--card)' }}
          >
            <div className="grid sm:grid-cols-2">
              <div className="flex min-w-0 flex-col px-1 pb-1 sm:pr-3">
                <p className="px-2 py-1 text-xs font-medium uppercase tracking-wide text-[var(--ink-muted)]">
                  Organizations
                </p>
                {!isLoaded ? <div className="mx-2 h-9 animate-pulse rounded bg-[var(--muted)]" /> : null}
                {userMemberships?.data?.map((membership) => (
                  <button
                    key={membership.id}
                    type="button"
                    disabled={working}
                    aria-current={
                      organization?.id === membership.organization.id ? 'true' : undefined
                    }
                    className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm hover:bg-[var(--muted)] ${
                      organization?.id === membership.organization.id ? 'bg-[var(--muted)]' : ''
                    }`}
                    onClick={() => void selectOrganization(membership.organization.id)}
                  >
                    {membership.organization.imageUrl ? (
                      <img
                        src={membership.organization.imageUrl}
                        alt=""
                        className="h-7 w-7 rounded-md object-cover"
                      />
                    ) : (
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[var(--muted)]">
                        <Building2 className="h-4 w-4" />
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {membership.organization.name}
                    </span>
                    {organization?.id === membership.organization.id ? (
                      <Check className="h-4 w-4 text-[var(--accent)]" />
                    ) : null}
                  </button>
                ))}
                {error ? <p className="px-2 py-1 text-xs text-[var(--destructive)]">{error}</p> : null}
              </div>

              <div className="mt-2 min-w-0 border-t border-[var(--border)] px-1 pt-2 sm:mt-0 sm:border-l sm:border-t-0 sm:pl-3 sm:pt-0">
                <div className="flex items-center justify-between gap-2 px-2 py-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-[var(--ink-muted)]">
                    Projects
                  </p>
                  {organization ? (
                    <a
                      href="/"
                      className="shrink-0 rounded-full border border-[var(--border)] px-2 py-0.5 text-[0.6875rem] font-medium text-[var(--ink-muted)] hover:bg-[var(--muted)] hover:text-[var(--ink)]"
                      onClick={() => setOpen(false)}
                    >
                      Browse all
                    </a>
                  ) : null}
                </div>
                {organization ? (
                  <>
                    {projects.length > PROJECT_SEARCH_THRESHOLD ? (
                      <div className="relative mx-1 mb-1 mt-1">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ink-muted)]" />
                        <Input
                          type="search"
                          value={projectQuery}
                          onChange={(event) => setProjectQuery(event.target.value)}
                          placeholder="Find a project"
                          aria-label="Find a project"
                          className="h-8 pl-8 text-sm"
                        />
                      </div>
                    ) : null}
                    <div className="max-h-64 overflow-y-auto overscroll-contain py-1">
                      {filteredProjects.map((candidate) => (
                        <a
                          key={candidate.id}
                          href={`/projects/${encodeURIComponent(candidate.slug)}`}
                          className="flex items-center gap-3 rounded-md px-2 py-2 text-sm text-[var(--ink)] hover:bg-[var(--muted)]"
                          onClick={() => setOpen(false)}
                        >
                          <span className="min-w-0 flex-1 truncate">{candidate.name}</span>
                          {candidate.slug === projectSlug ? (
                            <Check className="h-4 w-4 text-[var(--accent)]" />
                          ) : null}
                        </a>
                      ))}
                      {projects.length === 0 ? (
                        <p className="px-2 py-2 text-sm text-[var(--ink-muted)]">
                          No projects yet.
                        </p>
                      ) : null}
                      {projects.length > 0 && filteredProjects.length === 0 ? (
                        <p className="px-2 py-2 text-sm text-[var(--ink-muted)]">
                          No matching projects.
                        </p>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <p className="px-2 py-2 text-sm text-[var(--ink-muted)]">
                    Select an organization to see its projects.
                  </p>
                )}
              </div>
            </div>
          </div>
        </details>,
        titleSlot,
      )
    : null
}

/** Account chrome portaled into the reserved header account slot. */
export function PortalHeaderAccount() {
  const slot = useHeaderSlot(PORTAL_HEADER_ACCOUNT_SLOT_ID)
  const auth = useAuthActions()
  const { user, isLoaded: userLoaded } = useCurrentUser()
  const detailsRef = useRef<HTMLDetailsElement>(null)
  const [open, setOpen] = useState(false)
  const accountName =
    user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Account'

  useEffect(() => {
    if (!open) return

    function closeOnOutsidePointer(event: PointerEvent) {
      const target = event.target
      if (target instanceof Node && !detailsRef.current?.contains(target)) {
        setOpen(false)
      }
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setOpen(false)
      detailsRef.current?.querySelector('summary')?.focus()
    }

    document.addEventListener('pointerdown', closeOnOutsidePointer)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  if (!slot) return null

  return createPortal(
    <div className="flex shrink-0 items-center bg-[var(--card)]">
      <details
        ref={detailsRef}
        open={open}
        onToggle={(event) => setOpen(event.currentTarget.open)}
        className="group relative"
      >
        <summary
          className="flex h-9 w-9 cursor-pointer list-none items-center justify-center rounded-md transition hover:bg-[var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] [&::-webkit-details-marker]:hidden"
          aria-label="Open organization and account menu"
        >
          <Menu className="h-4 w-4 text-[var(--ink-muted)]" />
        </summary>
        <div
          className="absolute right-0 z-[70] mt-2 w-64 rounded-lg border border-[var(--border)] bg-[var(--card)] p-2 opacity-100 shadow-2xl"
          style={{ backgroundColor: 'var(--card)' }}
        >
          <a
            href="/members/me"
            className="flex min-h-12 w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm font-medium text-[var(--ink)] hover:bg-[var(--muted)]"
            onClick={() => {
              setOpen(false)
            }}
          >
            {!userLoaded ? (
              <>
                <span
                  className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-[var(--muted)]"
                  aria-hidden="true"
                />
                <span
                  className="h-4 w-28 animate-pulse rounded bg-[var(--muted)]"
                  aria-hidden="true"
                />
                <span className="sr-only">Loading account</span>
              </>
            ) : (
              <>
                {user?.imageUrl ? (
                  <img src={user.imageUrl} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                ) : (
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--muted)]">
                    <UserRound className="h-4 w-4 text-[var(--ink-muted)]" />
                  </span>
                )}
                <span className="min-w-0 truncate">{accountName}</span>
              </>
            )}
          </a>
          <ThemeMenuItem className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-sm font-medium text-[var(--ink)] hover:bg-[var(--muted)]" />
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm font-medium text-[var(--ink)] hover:bg-[var(--muted)]"
            onClick={() => {
              setOpen(false)
              void auth.signOut({ redirectUrl: '/sign-out' })
            }}
          >
            Sign out
          </button>
        </div>
      </details>
    </div>,
    slot,
  )
}
